// frontend/src/components/ScheduleManagement.js

import { useState, useEffect, useCallback } from 'react';
import axios from '../axiosConfig';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedCard } from './themed';
import RouteList from './RouteList';
import ScheduleOverview from './ScheduleOverview';
import ScheduleDetails from './ScheduleDetails';
import ScheduleSetup from './ScheduleSetup';
import { toast } from 'react-toastify';

const ScheduleManagement = ({ currentEntity }) => {
  const { t } = useTranslation();
  const { background, text, border, cn } = useTheme();
  // const [selectedPath, setSelectedPath] = useState(null);
  const [selectedRoute, setSelectRoute] = useState(null);  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null); // 'create', 'edit', 'copy'
  const [isCancelClicked, setIsCancelClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [allSchedules, setAllSchedules] = useState([]);
  const [appliedSchedules, setAppliedSchedules] = useState([]);
  const [availableSchedules, setAvailableSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
 
  // Reset selectedRoute and schedules when currentEntity changes
  useEffect(() => {
    setSelectRoute(null);
    setAppliedSchedules([]);
    setAvailableSchedules([]);
    if (currentEntity?.entityId) {
      fetchAllSchedules();
    }
  }, [currentEntity?.entityId]);


  useEffect(() => {
    if (currentEntity?.entityId && selectedRoute?.id) {
      fetchAppliedSchedules();
    } else {
      // Clear appliedSchedules if there's no selectedRoute
      setAppliedSchedules([]);
    }
  }, [currentEntity?.entityId, selectedRoute?.id]);

  useEffect(() => {
    // Filter out applied schedules from all schedules to get available schedules
    const appliedScheduleIds = appliedSchedules.map(schedule => schedule.id);
    const available = allSchedules.filter(schedule => !appliedScheduleIds.includes(schedule.id));
    setAvailableSchedules(available);
  }, [allSchedules, appliedSchedules]);

  const fetchAllSchedules = async () => {
    setIsLoading(true);
    
    try {
      const response = await axios.get(`/api/schedules/${currentEntity.entityId}`);
      setAllSchedules(response.data);
      console.log('Fetched schedules:', response.data);
    } catch (err) {
      console.error('Error fetching schedules:', err);
      toast.error(t('Failed to fetch schedules. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAppliedSchedules = async () => {
    if (!selectedRoute) return;

    setIsLoading(true);
    
    try {
      console.log('Selected route:', selectedRoute);
        const response = await axios.get(`/api/${currentEntity.entityId}/${selectedRoute.id}/route-schedules`);
      setAppliedSchedules(response.data);
      console.log('Fetched applied schedules:', response.data);
      
    } catch (err) {
      console.error('Error fetching applied schedules:', err);
      toast.error(t('Failed to fetch applied schedules. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const applySchedule = async (route_id, schedule_id, isActive = true, priority = 0) => {
/*     const targetRouteId = routeId || (selectedRoute?.id);
    if (!targetRouteId) {
      toast.error(t('NO RouteId detected, cannot apply the schedule.'));
      return;
    }

    setIsLoading(true); */

    const data = {
      route_id,
      schedule_id,
      active: isActive,
      priority
    };
    
    console.log('Applying schedule:', data);
    try {
      await axios.post(`/api/${currentEntity.entityId}/route-schedules`, data);
      // Refresh the applied schedules
      // await fetchAllSchedules();
      await fetchAppliedSchedules();
      // toast.success(t('Schedule applied successfully.'));


    } catch (error) {
      console.error('Error applying schedule:', error);
      toast.error(t('Failed to apply schedule. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const updateSchedulePriority = async (scheduleId, priority) => {
    try {
      setIsLoading(true);
      const data = {
        route_id: selectedRoute.id,
        schedule_id: scheduleId,
        priority: priority
      };
      await axios.put(`/api/${currentEntity.entityId}/route-schedules/priority`, data);
      await fetchAppliedSchedules();
      // toast.success(t('Schedule priority updated successfully.'));
    } catch (error) {
      // ... error handling ...
    } finally {
      setIsLoading(false);
    }
  };

  const unApplySchedule = async (scheduleId, routeId) => {
    const targetRouteId = routeId || (selectedRoute?.id);
    if (!targetRouteId) {
      return;
    }

    setIsLoading(true);
    // setErrorMessage(null);
    try {
      await axios.delete(`/api/${currentEntity.entityId}/route-schedules`, {
        data: {
          route_id: targetRouteId,
          schedule_id: scheduleId
        }
      });

      // await fetchAllSchedules();
      await fetchAppliedSchedules();
      // toast.success(t('Schedule unapplied successfully.'));
      // toast.success(`${selectedSchedule.name}`, t('unapplied successfully.'));
    } catch (error) {
      console.error('Error unapplying schedule:', error);
      toast.error(t('Failed to unapply schedule. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSchedule = async (entityId, scheduleId) => {
    console.log('Deleting schedule:', scheduleId);
    try {
      setIsLoading(true);
      await axios.delete(`/api/schedules/${entityId}/${scheduleId}`); // API endpoint for deleting a schedule
      toast.success(t('Schedule deleted successfully.'));
      await fetchAllSchedules(); // Re-fetch to get updated data
      
      // Clear selectedSchedule if it's the one deleted
      if (selectedSchedule && selectedSchedule.id === scheduleId) {
        setSelectedSchedule(null);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // Treat "not found" as success since the schedule is gone
        toast.success(t('Schedule deleted successfully or already removed.'));
        await fetchAllSchedules();
        if (selectedSchedule && selectedSchedule.id === scheduleId) {
          setSelectedSchedule(null);
        }
      } else {
        console.error('Delete error:', error.response?.status, error.response?.data);
        toast.error(t('Failed to delete schedule: ') + (error.response?.data?.message || error.message));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Modal control functions
  const openModal = useCallback((action, schedule = null) => {
    setModalAction(action);
    setSelectedSchedule(schedule);
    setIsModalOpen(true);
  }, []);

/*   const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setModalAction(null);
    setSelectedSchedule(null);
  }, []); */
  const closeModal = () => {
    setIsModalOpen(false);
    setModalAction(null);
    // Only close ScheduleDetails if cancel was not clicked
    if (!isCancelClicked) {
      setSelectedSchedule(null);
    }
  };

  // Handle cancel action from ScheduleSetup
  const handleCancel = (cancelStatus) => {
    setIsCancelClicked(cancelStatus);
    closeModal();
  };

  const handleScheduleCreatedOrUpdated = useCallback(async (newOrUpdatedSchedule) => {
    // Reset isCancelClicked to false since this is a save action
    setIsCancelClicked(false);

    if (modalAction === 'create' || modalAction === 'copy') {
      setAllSchedules(prevSchedules => [...prevSchedules, newOrUpdatedSchedule]);
    } else if (modalAction === 'edit') {
      setAllSchedules(prevSchedules => 
        prevSchedules.map(schedule => 
          schedule.id === newOrUpdatedSchedule.id ? newOrUpdatedSchedule : schedule
        )
      );
    // Update selectedSchedule to show the updated details
    // setSelectedSchedule(newOrUpdatedSchedule);
    }
    // Close only the ScheduleSetup modal
    closeModal();
    await fetchAllSchedules(); // Refresh the list of all schedules
  }, [modalAction, closeModal]);
  

  
  return (
    <div className={cn("min-h-screen", background.secondary)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className={cn("text-3xl font-bold", text.primary)}>{t('Schedules')}</h1>
          <p className={cn("mt-2", text.muted)}>{t('manageRouteSchedules')}</p>
        </div>

        <ThemedCard className="overflow-hidden">
          <div className="flex flex-col lg:flex-row h-[calc(100vh-200px)]">    
            <RouteList
              currentEntity={currentEntity}
              setselectRoute={setSelectRoute}
              selectedRoute={selectedRoute}
            />
          
            <ScheduleOverview
              selectedRoute={selectedRoute}
              availableSchedules={availableSchedules}
              onSelectSchedule={setSelectedSchedule}
              appliedSchedules={appliedSchedules}
              onCreateSchedule={() => openModal('create')}
              onApplySchedule={applySchedule}
              onUnApplySchedule={unApplySchedule}
              onUpdatePriority={updateSchedulePriority}
              onDeleteSchedule={deleteSchedule} 
              currentEntity={currentEntity}
              isLoading={isLoading}
              openEditModal={(schedule) => openModal('edit', schedule)}
              openCopyModal={(schedule) => openModal('copy', schedule)}
            />
            {selectedSchedule && (
              <ScheduleDetails
                schedule={selectedSchedule}
                onClose={() => setSelectedSchedule(null)}          
                onScheduleChanged={handleScheduleCreatedOrUpdated}
                currentEntity={currentEntity}
                onApply={applySchedule}
                onUnApply={unApplySchedule}
                selectedRoute={selectedRoute}
                isApplied={appliedSchedules.some(s => s.id === selectedSchedule.id)}
                currentPriority={
                  appliedSchedules.find(s => s.id === selectedSchedule.id)?.priority
                }
                onUpdatePriority={updateSchedulePriority}
                onSelectSchedule={setSelectedSchedule}
                appliedSchedules={appliedSchedules}
                onDeleteSchedule={deleteSchedule}           
                openEditModal={() => openModal('edit', selectedSchedule)}
                openCopyModal={() => openModal('copy', selectedSchedule)}
                onRefresh={fetchAllSchedules}
              />
            )}      

            {isModalOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
                <div className={cn(
                  "relative my-8 p-6 w-full md:w-3/4 max-w-4xl shadow-xl rounded-lg",
                  background.primary,
                  border.default,
                  "border"
                )}>
                  <div className="h-full">
                    <ScheduleSetup
                      currentEntity={currentEntity}
                      initialSchedule={
                        modalAction === 'create' 
                          ? {} 
                          : modalAction === 'copy' 
                            ? { ...selectedSchedule, name: `${selectedSchedule.name}` }
                            : selectedSchedule
                      }
                      isEditing={modalAction === 'edit'}
                      isCopying={modalAction === 'copy'}
                      onClose={closeModal}
                      onCancel={handleCancel}
                      onScheduleChanged={handleScheduleCreatedOrUpdated}
                    />
                  </div>  
                </div>
              </div>
            )}
          </div>
        </ThemedCard>
      </div>
    </div>
  );
};

export default ScheduleManagement;
